import { useEffect } from 'react'

/**
 * The header's contrast pass.
 *
 * The header is transparent and overlaps whatever is beneath it, so its text
 * has to be recoloured from that backdrop or it disappears. The capture did
 * this from an inline jQuery script; this is the same behaviour, without the
 * assumption that every section on the page is a Webflow `.section-container`.
 *
 * That assumption is why this moved out of `WebflowBinder`. The old pass
 * enumerated `.section-container`, did arithmetic to find which one the scroll
 * position fell inside, and read its `backgroundColor`. Three things were
 * wrong with it:
 *
 *   1. Half the site no longer uses that class. `Section` renders Tailwind
 *      utilities, so `/blog`, `/post/*` and `/support/*` had exactly one
 *      `.section-container` between them — the dark footer. Every one of those
 *      routes fell through to it and painted the nav white on a white page.
 *   2. `rgba(0, 0, 0, 0)` parses as three zeroes, so a transparent section
 *      scored luminance 0 and read as "dark" — white text, again on white.
 *   3. `--color-grayscale-10` is `#f8f8f8f5`: alpha 0.96. Reading it as opaque
 *      is close enough to work by luck, not by design.
 *
 * So the backdrop is found by hit-testing the point under the header rather
 * than by class, and resolved by compositing up the ancestor chain until it
 * reaches something opaque. It works the same on ported markup and on
 * component-built pages, which is the whole point.
 *
 * Checked against live on all 27 routes; 25 agree. The two that do not:
 *
 *   - `/roi-calculator` — live's own script matches a dark `.section-container`
 *     that is nowhere near the header and paints the nav white on a white
 *     page, so on live the logo and every link are invisible. Not reproduced.
 *   - `/outbound` — live captured a Webflow 404 for this URL, so our stub's
 *     dark hero has no live counterpart. Reading it as dark is correct.
 */

type Rgba = { r: number; g: number; b: number; a: number }

const WHITE: Rgba = { r: 255, g: 255, b: 255, a: 1 }

function parseColor(value: string): Rgba | null {
  const m = value.match(/[\d.]+/g)
  if (!m || m.length < 3) return null
  return {
    r: Number(m[0]),
    g: Number(m[1]),
    b: Number(m[2]),
    a: m.length > 3 ? Number(m[3]) : 1,
  }
}

/** `over` composited onto `under`, both premultiplied out to an opaque result. */
function composite(over: Rgba, under: Rgba): Rgba {
  const a = over.a + under.a * (1 - over.a)
  if (a === 0) return { r: 0, g: 0, b: 0, a: 0 }
  const mix = (o: number, u: number) => (o * over.a + u * under.a * (1 - over.a)) / a
  return { r: mix(over.r, under.r), g: mix(over.g, under.g), b: mix(over.b, under.b), a }
}

/**
 * The full-bleed ancestor of `el` — the page band it belongs to.
 *
 * Contrast is decided per section, not per element. Without this the nav reads
 * whatever card happens to be passing under it and flips as you scroll: on the
 * homepage a dark feature card would turn the nav white over an otherwise
 * white band. Page sections span the viewport and cards do not, so width is
 * the test, and it needs no class to work on either half of the site.
 */
function nearestBand(el: Element | null | undefined): Element | null {
  const full = window.innerWidth - 1
  for (let node: Element | null = el ?? null; node; node = node.parentElement) {
    if (node.getBoundingClientRect().width >= full) return node
  }
  return el ?? null
}

/**
 * The colour a viewer actually sees behind `el`: its own background stacked
 * over its ancestors', bottoming out at the page's white ground. Layers are
 * collected top-down and composited bottom-up, so a translucent section over a
 * dark one resolves to the blend rather than to either.
 */
function resolveBackdrop(el: Element | null): Rgba {
  const layers: Rgba[] = []
  for (let node = el; node; node = node.parentElement) {
    const c = parseColor(getComputedStyle(node).backgroundColor)
    if (c && c.a > 0) {
      layers.push(c)
      if (c.a >= 1) break
    }
  }
  return layers.reduceRight<Rgba>((under, over) => composite(over, under), WHITE)
}

/*
 * The capture's own weighting (YIQ), kept so its light/dark calls do not move.
 * A perceptual formula would flip a couple of mid-tone sections.
 */
function luminance({ r, g, b }: Rgba) {
  return 0.299 * r + 0.587 * g + 0.114 * b
}

const LIGHT_THRESHOLD = 128

export function useHeaderContrast(route: string) {
  useEffect(() => {
    const header = document.getElementById('header')
    if (!header) return

    /*
     * The header must overlap the section beneath it rather than occupy its own
     * row — the capture set this at runtime too:
     *   $header.css("margin-bottom", `-${$header.outerHeight()}px`)
     * Without it a white band appears between the promo strip and the hero.
     */
    header.style.marginBottom = `-${header.offsetHeight}px`

    /** What sits under the header, ignoring the header and promo strip. */
    const backdropUnderHeader = (): Rgba => {
      const promo = document.getElementById('promo-header')
      const headerBox = header.getBoundingClientRect()
      const x = Math.round(window.innerWidth / 2)
      const y = Math.round(headerBox.top + headerBox.height / 2)
      if (y < 0 || y > window.innerHeight) return WHITE

      const hit = document
        .elementsFromPoint(x, y)
        .find((el) => !header.contains(el) && !(promo && promo.contains(el)))
      return resolveBackdrop(nearestBand(hit) ?? document.body)
    }

    const apply = () => {
      const promo = document.getElementById('promo-header')
      const threshold = promo ? promo.getBoundingClientRect().height + 5 : 5
      const atTop = window.scrollY < threshold

      const backdrop = backdropUnderHeader()
      const onLight = luminance(backdrop) > LIGHT_THRESHOLD

      /*
       * Transparent at the top so the hero reads as one piece; once scrolled it
       * takes the backdrop at 94% behind a blur, which is what the capture did.
       */
      const { r, g, b } = backdrop
      const fill = atTop
        ? 'transparent'
        : `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, 0.94)`
      const blur = atTop ? 'none' : 'blur(8px)'

      header.style.backgroundColor = fill
      header.style.backdropFilter = blur
      header.style.setProperty('-webkit-backdrop-filter', blur)

      /*
       * One custom property, and chrome.css spends it. This used to walk all
       * 174 header descendants setting `style.color` on each — which React
       * quietly undid: on component-built routes the nav's children re-render
       * after this pass, so every inline colour was wiped while the header's
       * own inline background survived. The result was a header that had
       * measured its backdrop correctly and then lost the answer.
       *
       * A property on the header inherits to descendants and cannot be lost
       * that way, because the header element itself is never re-created.
       */
      header.style.setProperty('--header-ink', onLight ? '#000' : '#fff')
      header.classList.toggle('dark', !onLight)
    }

    /* Coalesce scroll and resize into one pass per frame. */
    let frame = 0
    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        apply()
      })
    }

    /*
     * The chrome mounts before the route's own content does — page templates
     * are lazy, so this effect runs against a page that is still empty and
     * measures the white ground under an unrendered hero. Watching the body
     * box covers that commit, and equally a font or hero image landing, the
     * promo strip being dismissed, or a filter changing the page height.
     * Without it the first measurement is also the last, and every route whose
     * hero arrives late keeps the wrong answer.
     */
    const observer = new ResizeObserver(schedule)
    observer.observe(document.body)

    apply()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      observer.disconnect()
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [route])
}
